import { geoLocationsAPI } from '../../lib/api';

import { parseCoordinateInput } from './geo';

/**
 * Finding a place for the zone picker, in the order that costs least.
 *
 * 1. Coordinates pasted straight in — a border post is often handed over as a
 *    pair of numbers, and that path needs no network at all.
 * 2. The organisation's own referential, which is where a zone being edited or
 *    duplicated already lives.
 * 3. A geocoder, and only then.
 */

/**
 * Nominatim's usage policy forbids bulk and type-ahead querying; it allows a
 * search a human explicitly asked for. The lookup is therefore fired on submit
 * only, never on keystroke, and never more than once a second. Point
 * REACT_APP_GEOCODER_URL at a self-hosted instance or a backend proxy before
 * this sees real volume.
 */
const GEOCODER_URL =
  process.env.REACT_APP_GEOCODER_URL || 'https://nominatim.openstreetmap.org/search';
const GEOCODER_MIN_INTERVAL_MS = 1100;

let lastGeocodeAt = 0;

export async function geocodePlace(query, { signal } = {}) {
  const text = (query || '').trim();
  if (text.length < 3) return [];

  const now = Date.now();
  if (now - lastGeocodeAt < GEOCODER_MIN_INTERVAL_MS) return [];
  lastGeocodeAt = now;

  const url = `${GEOCODER_URL}?format=jsonv2&limit=5&q=${encodeURIComponent(text)}`;
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) return [];
  const rows = await response.json();
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row) => ({
      id: `geo-${row.place_id}`,
      name: (row.display_name || '').split(',')[0] || text,
      subtitle: row.display_name || '',
      latitude: Number(row.lat),
      longitude: Number(row.lon),
      origin: 'geocoder',
    }))
    .filter((row) => Number.isFinite(row.latitude) && Number.isFinite(row.longitude));
}

export async function searchZoneReferential(query) {
  const rows = await geoLocationsAPI.getAll({ q: query, limit: 5 });
  const list = Array.isArray(rows) ? rows : rows?.data || [];
  return list
    .filter((row) => row?.latitude != null && row?.longitude != null)
    .map((row) => ({
      id: `ref-${row.id}`,
      name: row.name,
      subtitle: [row.type, row.country, row.city].filter(Boolean).join(' · '),
      latitude: Number(row.latitude),
      longitude: Number(row.longitude),
      radius_meters: Number(row.radius_meters) || undefined,
      origin: 'referential',
    }));
}

export default async function searchLocations(query, { signal } = {}) {
  const text = (query || '').trim();
  if (!text) return [];

  const coordinates = parseCoordinateInput(text);
  if (coordinates) {
    return [
      {
        id: 'coords',
        name: `${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}`,
        subtitle: 'Coordonnées saisies',
        latitude: coordinates[0],
        longitude: coordinates[1],
        origin: 'coordinates',
      },
    ];
  }

  const referential = await searchZoneReferential(text).catch(() => []);
  if (referential.length > 0) return referential;

  return geocodePlace(text, { signal }).catch(() => []);
}
